import React, { useEffect, useState } from "react";
import { Phone, Mail, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { company, navLinks, brand } from "../data/mock";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname, hash]);

  const isActive = (to) => {
    if (to === "/") return pathname === "/";
    if (to.startsWith("/#")) return pathname === "/";
    return pathname.startsWith(to);
  };

  return (
    <>
      {/* Top utility bar */}
      <div className="hidden lg:flex items-center justify-between text-xs px-8 py-2 border-b border-white/5 bg-black/40 backdrop-blur-md fixed top-0 left-0 right-0 z-40">
        <div className="flex items-center gap-6 text-white/70">
          <a href={`tel:${company.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-white transition-colors">
            <Phone className="w-3.5 h-3.5 text-amber-accent" />
            <span className="font-medium tracking-wide">{company.phone}</span>
          </a>
          <a href={`mailto:${company.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
            <Mail className="w-3.5 h-3.5 text-amber-accent" />
            <span>{company.email}</span>
          </a>
        </div>
        <div className="text-white/55">{company.hours}</div>
      </div>

      {/* Main header */}
      <header
        className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "top-0 bg-black/85 backdrop-blur-xl border-b border-white/10 py-3"
            : "lg:top-9 top-0 bg-transparent py-4"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={brand.logoWhite}
              alt="R&C"
              className="h-12 lg:h-14 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
            />
            <div className="hidden md:block leading-tight border-l border-white/15 pl-3 ml-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">Public & Civil Engineers</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5D000]">Since 1957</div>
            </div>
          </Link>

          {/* Nav desktop */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) =>
              l.to.startsWith("/#") ? (
                <a
                  key={l.label}
                  href={pathname === "/" ? l.to.replace("/", "") : l.to}
                  className="text-sm font-medium text-white/75 hover:text-white link-shimmer"
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.label}
                  to={l.to}
                  className={`text-sm font-medium link-shimmer ${
                    isActive(l.to) ? "text-[#F5D000]" : "text-white/75 hover:text-white"
                  }`}
                >
                  {l.label}
                </Link>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href="/#contact" className="btn-amber px-5 py-2.5 rounded-md text-sm font-semibold">
              Get a Quote
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 rounded-md border border-white/15 flex items-center justify-center text-white"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden mt-3 mx-4 rounded-xl border border-white/10 bg-black/95 backdrop-blur-xl p-5">
            <div className="flex flex-col gap-1">
              {navLinks.map((l) =>
                l.to.startsWith("/#") ? (
                  <a key={l.label} href={pathname === "/" ? l.to.replace("/", "") : l.to} className="py-3 text-white/85 border-b border-white/5">
                    {l.label}
                  </a>
                ) : (
                  <Link key={l.label} to={l.to} className="py-3 text-white/85 border-b border-white/5">
                    {l.label}
                  </Link>
                )
              )}
              <a href={`tel:${company.phone}`} className="mt-4 btn-amber px-4 py-3 rounded-md text-sm font-semibold text-center">
                Call {company.phone}
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
